import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Calendar, ChevronRight, Trash2 } from 'lucide-react';
import { useIntelligence } from '../contexts';
import api from '../../services/api';
import type { Conversation } from '../types';
import { PageHeader, primaryBtnCls } from '../../ui';

export function History() {
  const { conversations, loadConversations, setCurrentConversation } = useIntelligence();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadConversations();
      setLoading(false);
    };
    load();
  }, [loadConversations]);

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      try {
        await api.delete(`/api/intelligence/conversations/${id}`);
        await loadConversations();
      } catch (error) {
        console.error('Error deleting conversation:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        className="mb-6"
        title="Chat History"
        icon={MessageSquare}
        subtitle="View and continue your previous conversations"
      />

      {conversations.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <MessageSquare size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No conversations yet</h3>
          <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">Start asking questions to build your history</p>
          <Link
            to="/intelligence/chat"
            className={primaryBtnCls}
          >
            <MessageSquare size={16} />
            Start a conversation
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {conversations.map((conversation) => (
            <Link
              key={conversation.id}
              to="/intelligence/chat"
              onClick={() => setCurrentConversation(conversation)}
              className="block bg-white rounded-xl border border-gray-200 p-4 transition-all duration-150 hover:border-primary-300 hover:shadow-md group dark:bg-gray-800 dark:border-gray-700 dark:hover:border-primary-700"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 dark:bg-primary-900/40">
                    <MessageSquare size={20} className="text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-gray-900 truncate dark:text-gray-100">
                      {conversation.title || 'Untitled conversation'}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5 dark:text-gray-400">
                      <Calendar size={14} />
                      <span>{formatDate(conversation.updated_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleDelete(conversation.id, e)}
                    className="p-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all dark:hover:text-red-400"
                    title="Delete conversation"
                  >
                    <Trash2 size={18} />
                  </button>
                  <ChevronRight size={20} className="text-gray-400" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
